/**
 * authContext.js — single source of truth for the signed-in user, their
 * roles, AND (new in the /profile feature) their editable profile + the
 * "is-this-a-fresh-signup?" flag used to gate the onboarding wizard.
 *
 * Auth refactor 2026-05-27:
 *   - All backend calls go through apiCall() so the verified Firebase ID
 *     token is attached automatically. Server endpoints derive uid from
 *     that token; we never send a client-trusted uid in the body anymore.
 *
 * Profile feature 2026-05-27:
 *   - On every onAuthStateChanged login, we call syncUserToDatabase.php
 *     (which now returns is_new_user when it inserts the row) and then
 *     getUserProfile.php (which returns { profile, stats }).
 *   - isNewUser is true ONLY for the response of the very first
 *     syncUserToDatabase call that inserted the row. As soon as the
 *     wizard finishes (or the user clicks Skip), the caller flips it back
 *     to false via markOnboardingComplete(). It does not reappear on
 *     reload because users.onboarded_at is now populated.
 *   - refreshProfile() is exposed so ProfilePage and the wizard can pull
 *     a fresh copy after they POST to updateUserProfile.php (single
 *     source-of-truth pattern; we never optimistic-update the local copy
 *     without re-reading the row, to keep manually_edited_at semantics
 *     correct across tabs).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { apiCall } from './api';

// Create the context
const AuthContext = createContext();

const getFirstName = (displayName) => displayName ? displayName.split(' ')[0] : '';
const getLastName = (displayName) => {
    const nameParts = displayName ? displayName.split(' ') : [];
    return nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
};

// AuthProvider component to wrap the app
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userFirstName, setUserFirstName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [globalRoles, setGlobalRoles] = useState({ isAdmin: false });
    const [projectRoles, setProjectRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exhibitionMode, setExhibitionMode] = useState(false);
    // Profile state added with the /profile feature. `profile` is the
    // canonical users-row shape returned by getUserProfile.php; `stats`
    // is the {posts, locations} pair used by ProfilePage's number cards.
    const [profile, setProfile] = useState(null);
    const [stats, setStats] = useState({ posts: 0, locations: 0 });
    // isNewUser flips true exactly once per signup — when syncUserToDatabase.php
    // reports it just INSERTed the row. The wizard reads it; markOnboardingComplete()
    // flips it back to false.
    const [isNewUser, setIsNewUser] = useState(false);
    // authError surfaces a row-create failure that would otherwise be silently
    // swallowed. When syncUserToDatabase does NOT return success, we can't
    // create/confirm the users row, so we don't open the wizard (see the
    // wizard-gate below) — but we also must not leave the user "logged in with
    // no account and no feedback". Consumers (MapComponent) show this as a
    // toast; it's always console.error'd too so a future regression is visible.
    const [authError, setAuthError] = useState(null);

    // Sync user to the database. Returns the parsed JSON response so the
    // caller can read `is_new_user` and gate the onboarding wizard.
    const syncUserToDatabase = async (firebaseUser) => {
        if (!firebaseUser) return null;
        try {
            // Auth refactor: no uid in the body — server reads it from the
            // verified ID token. We still pass displayName/email/photoURL so
            // syncUserToDatabase.php can do the conditional re-sync (the
            // ID token doesn't always carry the latest photoURL claim).
            const response = await apiCall('syncUserToDatabase.php', {
                method: 'POST',
                body: {
                    displayName: firebaseUser.displayName,
                    email: firebaseUser.email,
                    photoURL: firebaseUser.photoURL,
                },
            });
            if (response && response.success === false) {
                console.error('Error syncing user data:', response.error);
            }
            return response;
        } catch (error) {
            console.error('Error syncing user data:', error);
            return null;
        }
    };

    // Fetch user roles from the database. uid was previously in the query
    // string; the server now derives it from the verified token, so the
    // client doesn't need to (and shouldn't) send it.
    const getUserRolesFromDatabase = async () => {
        try {
            return await apiCall('fetchUserProjectRoles.php');
        } catch (error) {
            console.error('Failed to retrieve user roles:', error);
            return { globalRoles: { isAdmin: false }, projectRoles: [] };
        }
    };

    // Fetch the editable profile + contribution stats. Exposed via the
    // context so ProfilePage and the wizard can refetch after a successful
    // PATCH instead of optimistic-merging in place.
    const refreshProfile = useCallback(async () => {
        try {
            const response = await apiCall('getUserProfile.php');
            if (response && response.profile) {
                setProfile(response.profile);
                setStats(response.stats || { posts: 0, locations: 0 });
                return response;
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        }
        return null;
    }, []);

    // Called by the onboarding wizard (and by Skip) so the wizard does
    // not re-appear next render. We do NOT mutate users.onboarded_at here
    // — updateUserProfile.php already wrote it as a side effect of the
    // wizard's PATCH. This just flips the local flag.
    const markOnboardingComplete = useCallback(() => {
        setIsNewUser(false);
    }, []);

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            // In exhibition mode, always treat user as unauthenticated
            if (exhibitionMode) {
                console.log('Exhibition mode: ignoring authenticated user');
                setUser(null);
                setUserFirstName('');
                setUserLastName('');
                setGlobalRoles({ isAdmin: false });
                setProjectRoles([]);
                setProfile(null);
                setStats({ posts: 0, locations: 0 });
                setIsNewUser(false);
                setAuthError(null);
                setLoading(false);
                return;
            }

            if (firebaseUser) {
                try {
                    // Order matters: sync first (creates the users row if
                    // missing), THEN fetch roles + profile against the row
                    // we just upserted. syncUserToDatabase returns is_new_user
                    // on first-ever insert; we record that so the wizard
                    // can mount.
                    const syncResp = await syncUserToDatabase(firebaseUser);
                    const wizardNeeded = Boolean(syncResp && syncResp.is_new_user);
                    // A missing response (network/throw) or an explicit
                    // success:false means the users row could not be
                    // created/confirmed. Don't swallow it: record a
                    // user-facing error so we never repeat the "logged in but
                    // no row, no wizard, no feedback" masking regression.
                    const syncFailed = !syncResp || syncResp.success === false;

                    const rolesResp = await getUserRolesFromDatabase();
                    const fetchedGlobalRoles = rolesResp?.globalRoles || { isAdmin: false };
                    const fetchedProjectRoles = rolesResp?.projectRoles || [];

                    // Also pull the profile up-front; ProfilePage will hit
                    // the same endpoint when it mounts but caching it on
                    // login removes a round-trip on /profile navigation.
                    const profileResp = await apiCall('getUserProfile.php').catch((e) => {
                        console.error('Error fetching profile on login:', e);
                        return null;
                    });

                    // Gate the onboarding wizard on an ACTUAL users row.
                    //
                    // Bug (client, Jul 2026): a new partner hit "User row not
                    // found" on every wizard step and could never enable
                    // Finish. Root cause: when syncUserToDatabase.php fails to
                    // create the row (most commonly because the email already
                    // exists under a different firebase_uid — users.email is
                    // UNIQUE, so the INSERT throws a duplicate-key error),
                    // getUserProfile.php 404s, so `profileResp` is null and
                    // `onboarded_at` reads back as `undefined`. The old check
                    // treated `undefined` identically to `null` and opened the
                    // wizard anyway — but with no row to write to, every
                    // updateUserProfile.php PATCH 404s ("User row not found"),
                    // trapping the user in an unusable wizard.
                    //
                    // Fix: only open the wizard when we can PROVE a row exists —
                    // either sync just INSERTed one (wizardNeeded), or
                    // getUserProfile returned a row whose onboarded_at IS NULL
                    // (the legit cross-device / abandoned-mid-flow re-prompt).
                    // A missing/failed profile fetch (`undefined`) must NOT open
                    // the wizard.
                    const profileRow = profileResp?.profile || null;
                    const stillNeedsOnboarding =
                        wizardNeeded ||
                        (profileRow ? profileRow.onboarded_at === null : false);

                    console.log('User roles:', { globalRoles: fetchedGlobalRoles, projectRoles: fetchedProjectRoles });
                    setUser(firebaseUser);
                    setUserFirstName(getFirstName(firebaseUser.displayName));
                    setUserLastName(getLastName(firebaseUser.displayName));
                    setGlobalRoles(fetchedGlobalRoles);
                    setProjectRoles(fetchedProjectRoles);
                    if (profileResp) {
                        setProfile(profileResp.profile || null);
                        setStats(profileResp.stats || { posts: 0, locations: 0 });
                    }
                    setIsNewUser(stillNeedsOnboarding);

                    if (syncFailed) {
                        console.error(
                            'syncUserToDatabase did not succeed — the account row may be missing. Response:',
                            syncResp
                        );
                        setAuthError(
                            "We couldn't finish setting up your account. Please refresh and try again, or contact support if this keeps happening."
                        );
                    } else {
                        setAuthError(null);
                    }
                } catch (error) {
                    console.error("Error syncing user or fetching roles:", error);
                    setAuthError(
                        "We couldn't finish setting up your account. Please refresh and try again, or contact support if this keeps happening."
                    );
                }
            } else {
                setUser(null);
                setUserFirstName('');
                setUserLastName('');
                setGlobalRoles({ isAdmin: false });
                setProjectRoles([]);
                setProfile(null);
                setStats({ posts: 0, locations: 0 });
                setIsNewUser(false);
                setAuthError(null);
            }
            setLoading(false);
        });

        return () => unsubscribe(); // Clean up the listener on unmount
    }, [exhibitionMode]);

    const logout = async () => {
        const auth = getAuth();
        await signOut(auth);
        setUser(null);
        setUserFirstName('');
        setUserLastName('');
        setProfile(null);
        setStats({ posts: 0, locations: 0 });
        setIsNewUser(false);
        setAuthError(null);
    };

    // Let a consumer dismiss the auth error (e.g. after showing the toast).
    const clearAuthError = useCallback(() => setAuthError(null), []);

    return (
        <AuthContext.Provider value={{
            user,
            userFirstName,
            userLastName,
            globalRoles,
            projectRoles,
            loading,
            logout,
            setExhibitionMode,
            // /profile additions:
            profile,
            stats,
            isNewUser,
            authError,
            clearAuthError,
            refreshProfile,
            markOnboardingComplete,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);
